import React from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppDraggableList from "components/Shared/AppDraggableList";
import ListOption from "components/ListOption/ListOptionItem";
import ListOptionForm from "components/ListOption/ListOptionForm";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import FormDrawer from "components/Shared/FormDrawer";

import {
  useAddGlobalListMutation,
  useEditGlobalListMutation,
  useLazyDetailGlobalListQuery,
  useReOrderGlobalListMutation,
} from "store/services/globalListApi";

import { useAuth, useAppMeta, useReOrderDraggableList } from "hooks";

import { GLOBAL_LIST_OPTIONS } from "data/static";

const GlobalListOptionDetailScreen = () => {
  const [showForm, setShowForm] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(null);

  const { id: listId } = useParams();

  const [searchParams] = useSearchParams();

  const { viewEntity } = useAuth();

  const listTypeParams = React.useMemo(() => {
    return searchParams.get("type")?.split(",");
  }, [searchParams]);

  useAppMeta(
    !listTypeParams?.length
      ? "Global List Detail"
      : `Global List - ${listTypeParams.at(-1)}`
  );

  const currentListType = React.useMemo(() => {
    return listTypeParams[listTypeParams?.length - 1];
  }, [listTypeParams]);

  const staticListDetail = GLOBAL_LIST_OPTIONS.find(
    (lo) => lo.name === currentListType
  );

  const optionBreadcrumbs = React.useMemo(
    () =>
      listTypeParams?.reduce((acc, param, index) => {
        if (listTypeParams.length === index) {
          acc.push({ title: param });
          return acc;
        }

        const _typeQuery = listTypeParams
          ?.slice(index, listTypeParams?.length - 1)
          .join(",");

        const path = `/app/crm/global-list/detail/${listTypeParams[0]}?type=${_typeQuery}`;

        acc.push({
          title: param,
          path,
        });

        return acc;
      }, []),
    [listTypeParams]
  );

  const breadcrumbs = [
    { title: "Global List", path: "/app/crm/global-list" },
    ...optionBreadcrumbs,
  ];

  //   RTK Query
  const [
    fetchGlobalListDetail,
    {
      isLoading: loadingGlobalList,
      isFetching: fetchingGlobalList,
      data: listDetail,
      error,
    },
  ] = useLazyDetailGlobalListQuery();

  const [addGlobalList, { isLoading: addingGlobalOption }] =
    useAddGlobalListMutation();

  const [editGlobalList, { isLoading: updatingGlobalOption }] =
    useEditGlobalListMutation();

  const [reOrderGlobalList, { isLoading: reOrderingOptions }] =
    useReOrderGlobalListMutation();

  const serialNumberInitials = React.useMemo(() => {
    if (!listDetail?.name) return;
    return listDetail?.name
      ?.split(" ")
      .map((el) => el.charAt(0)?.toUpperCase())
      ?.join("");
  }, [listDetail?.name]);

  const { orderedList, handleOnDragEnd } = useReOrderDraggableList({
    listItems: listDetail?.children,
    serialNumberInitials,
  });

  // methods
  const handleClose = () => {
    setShowForm(false);
    setSelectedOption(null);
  };

  const handleAddOption = async (data) => {
    if (!data) return;

    if (GLOBAL_LIST_OPTIONS?.find((o) => o.value === data?.name))
      return toast.error(
        "Option name cannot be one of the predefined list name"
      );

    const payload = {
      ...data,
      isSubtype: true,
      level: listDetail.level ? listDetail.level + 1 : 1,
      maxLevel: listDetail?.maxLevel || 2,
      type: currentListType,
      ...(listDetail?._id && {
        parent: listDetail?._id,
      }),
      parentName: !listDetail?._id ? listDetail?.value : "",
    };

    try {
      await addGlobalList(payload).unwrap();
      toast.success("Option successfully added!");
      handleClose();
    } catch (err) {
      const errorMessage =
        err?.data?.message || "Couldn't add option to the list";
      toast.error(errorMessage);
    }
  };

  const handleUpdateOption = async (data) => {
    if (!selectedOption) return;

    const payload = {
      ...selectedOption,
      id: selectedOption._id,
      ...data,
    };

    try {
      await editGlobalList(payload).unwrap();
      toast.success("Option successfully updated!");
      handleClose();
    } catch (err) {
      const errorMessage = err?.data?.message || "Couldn't update option";
      toast.error(errorMessage);
    }
  };

  const handleReorderOptions = async () => {
    const reorderedList = [...orderedList]?.map(({ _id, serialNumber }) => ({
      _id,
      serialNumber,
    }));

    try {
      await reOrderGlobalList({ reorderedList }).unwrap();
      toast.success("List successfully reordered!");
    } catch (err) {
      toast.err(err?.data?.message ?? "Couldn't reorder global list options");
    }
  };

  React.useEffect(() => {
    fetchGlobalListDetail({ _id: listId });
  }, [fetchGlobalListDetail, listId, viewEntity]);

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={loadingGlobalList || fetchingGlobalList}
      headerActions={
        <div className="flex gap-4">
          {!!orderedList?.length && (
            <LoadingButton
              loading={reOrderingOptions}
              loadingIndicator="Saving..."
              variant="outlined"
              color="success"
              onClick={handleReorderOptions}
            >
              Save Order
            </LoadingButton>
          )}

          <Button
            disabled={reOrderingOptions}
            startIcon={<Add />}
            variant="contained"
            onClick={() => setShowForm(true)}
          >
            New Option
          </Button>
        </div>
      }
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        {!loadingGlobalList && !fetchingGlobalList ? (
          <AppDraggableList
            isBusy={reOrderingOptions}
            onDragEnd={handleOnDragEnd}
            listItems={orderedList}
            draggableItem={({ data }) => (
              <GlobalListOptionItem
                data={data}
                onSelectOption={(option) => {
                  setShowForm(true);
                  setSelectedOption(option);
                }}
              />
            )}
          />
        ) : (
          <RowsSkeleton />
        )}
      </BreadcrumbLayout.Paper>

      <FormDrawer
        title={` Global List Option - ${currentListType}`}
        open={showForm}
        onClose={handleClose}
      >
        <ListOptionForm
          isBusy={addingGlobalOption || updatingGlobalOption}
          option={selectedOption}
          groupOptions={staticListDetail?.groups}
          onSubmit={(data) =>
            selectedOption ? handleUpdateOption(data) : handleAddOption(data)
          }
          onClose={handleClose}
        />
      </FormDrawer>
    </BreadcrumbLayout>
  );
};

export default GlobalListOptionDetailScreen;

const GlobalListOptionItem = ({ data, onSelectOption = () => {} }) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  // RTK Query
  const [toggleStatus, { isLoading: togglingStatus }] =
    useEditGlobalListMutation();

  // methods
  const handleToggleStatus = async (option) => {
    if (!option) return;

    const toggleOff = option.status === "Active";

    const payload = {
      ...option,
      id: option._id,
      status: toggleOff ? "Inactive" : "Active",
    };

    try {
      await toggleStatus(payload).unwrap();
      toast.success(
        `Option successfully ${toggleOff ? "deactivated" : "activated"}!`
      );
    } catch (err) {
      const errorMessage =
        err?.data?.message ||
        `Couldn't ${toggleOff ? "deactivate" : "activate"} option`;
      toast.error(errorMessage);
    }
  };

  return (
    <ListOption
      isBusy={togglingStatus}
      listOption={data}
      isActive={data?.status === "Active"}
      onSelectOption={onSelectOption}
      onToggleOptionStatus={handleToggleStatus}
      onViewSubOptions={() =>
        navigate(
          `/app/crm/global-list/detail/${data?._id}?type=${[
            searchParams.get("type"),
            data?.name,
          ]}`
        )
      }
    />
  );
};
